import { useState, useEffect } from "react";
import { useTheme } from "next-themes";
import { Sun, Moon, Monitor } from "lucide-react";

interface ThemeToggleProps {
  variant?: "dropdown" | "button";
  showLabel?: boolean;
}

/**
 * ThemeToggle - Allows users to switch between light, dark, and system themes
 */
const ThemeToggle: React.FC<ThemeToggleProps> = ({
  variant = "dropdown",
  showLabel = true,
}) => {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  // Prevent hydration mismatch by waiting until mounted
  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    // Return placeholder to prevent layout shift
    return (
      <div className="flex items-center gap-2.5 px-3 py-2.5">
        <div className="w-4 h-4" />
        {showLabel && <span className="text-sm font-medium">Theme</span>}
      </div>
    );
  }

  if (variant === "button") {
    // Simple button that cycles through themes
    const cycleTheme = () => {
      if (theme === "light") setTheme("dark");
      else if (theme === "dark") setTheme("system");
      else setTheme("light");
    };

    return (
      <button
        onClick={cycleTheme}
        className="p-2 rounded-xl transition-all hover:bg-orange-50 dark:hover:bg-white/10"
        title={`Current: ${theme} (click to cycle)`}
      >
        {resolvedTheme === "dark" ? (
          <Moon className="w-5 h-5" style={{ color: "var(--st-primary)" }} />
        ) : (
          <Sun className="w-5 h-5" style={{ color: "var(--st-primary)" }} />
        )}
      </button>
    );
  }

  // Dropdown variant - shows all three options
  const themes = [
    { value: "light", label: "Light", icon: Sun },
    { value: "dark", label: "Dark", icon: Moon },
    { value: "system", label: "System", icon: Monitor },
  ] as const;

  return (
    <div className="py-1">
      {themes.map(({ value, label, icon: Icon }) => (
        <button
          key={value}
          onClick={() => setTheme(value)}
          className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-all ${
            theme === value
              ? "bg-orange-50 dark:bg-white/10"
              : "hover:bg-orange-50 dark:hover:bg-white/10"
          }`}
        >
          <Icon
            className="w-4 h-4"
            style={{
              color: theme === value ? "var(--st-primary)" : "var(--st-text-muted)",
            }}
          />
          <span
            className="text-sm font-medium"
            style={{
              fontFamily: "Quicksand, sans-serif",
              color: theme === value ? "var(--st-primary)" : "var(--st-text-heading)",
            }}
          >
            {label}
          </span>
          {theme === value && (
            <span
              className="ml-auto text-xs"
              style={{ color: "var(--st-primary)" }}
            >
              ✓
            </span>
          )}
        </button>
      ))}
    </div>
  );
};

export default ThemeToggle;
